import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, Building2, FileText, Scale, Clock, Phone, ChevronRight, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import axios from 'axios';
import PageSEO from '../components/PageSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const MAYOR_PHOTO = "https://customer-assets.emergentagent.com/job_e685b0f8-6e4a-4c34-8412-b6505cc3b7ac/artifacts/i579cojd_mr%20le%20maire%201.jpg";

const ICON_MAP = { FileText, Building2, Scale, Users };

export const MairiePage = () => {
  const [council, setCouncil] = useState([]);
  const [settings, setSettings] = useState(null);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [councilRes, settingsRes, pageRes] = await Promise.all([
        axios.get(`${API}/council`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/pages/mairie`),
      ]);
      setCouncil(councilRes.data);
      setSettings(settingsRes.data);
      setPage(pageRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getSection = (key) => page?.sections?.find(s => s.key === key);

  const maireBio = getSection('maire_bio')?.content;
  const motDuMaireCMS = getSection('mot_du_maire')?.content;
  const maireNom = getSection('maire_nom')?.content;
  const missions = getSection('missions')?.items || [];

  const services = getSection('services_municipaux')?.items || [];
  const coordonnees = getSection('coordonnees')?.content || '+221 XX XXX XX XX';

  const procedureCategories = ['procedures_etat_civil', 'procedures_certificats', 'procedures_urbanisme', 'procedures_fiscalite'];
  const procedures = procedureCategories
    .map(key => getSection(key))
    .filter(Boolean)
    .map(s => ({
      category: s.title,
      items: (s.items || []).map(item => ({
        title: item.title,
        delay: item.delay,
        documents: (item.documents || '').split('|').filter(Boolean),
      }))
    }));

  const guichetDiaspora = getSection('guichet_diaspora')?.content || '';

  const mayorMessage = motDuMaireCMS || settings?.mayor_message || `Chères concitoyennes, Chers concitoyens, Chers amis de Gandiaye et de la Diaspora,

C'est avec une immense fierté et un sens profond du devoir que je vous souhaite la bienvenue sur le portail numérique officiel de notre commune.

Bonne navigation à toutes et à tous !`;

  return (
    <main className="pt-14 md:pt-16" data-testid="mairie-page">
      <PageSEO
        title="Ma Mairie"
        description="Découvrez l'administration municipale de Gandiaye : mot du maire, conseil municipal, services et démarches administratives."
        path="/mairie"
      />
      {/* Hero */}
      <section className="bg-emerald-600 py-16 md:py-24">
        <div className="container-main">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            Ma Mairie
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Transparence, proximité et service aux citoyens. Découvrez votre administration municipale.
          </p>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="py-12 bg-white" data-testid="mairie-tabs">
        <div className="container-main">
          <Tabs defaultValue="maire" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-start gap-2 bg-neutral-100 p-2 rounded-xl mb-8">
              <TabsTrigger value="maire" className="flex-1 md:flex-none" data-testid="tab-maire">
                Mot du Maire
              </TabsTrigger>
              <TabsTrigger value="conseil" className="flex-1 md:flex-none" data-testid="tab-conseil">
                Conseil Municipal
              </TabsTrigger>
              <TabsTrigger value="services" className="flex-1 md:flex-none" data-testid="tab-services">
                Services
              </TabsTrigger>
              <TabsTrigger value="demarches" className="flex-1 md:flex-none" data-testid="tab-demarches">
                Démarches
              </TabsTrigger>
            </TabsList>

            {/* Mot du Maire */}
            <TabsContent value="maire">
              <div className="grid lg:grid-cols-3 gap-12">
                <div className="lg:col-span-1">
                  <div className="sticky top-32">
                    <img 
                      src={settings?.mayor_photo || MAYOR_PHOTO}
                      alt="Le Maire Pape Songde Diop"
                      loading="lazy"
                      className="rounded-2xl shadow-xl w-full"
                    />
                    <div className="mt-6 text-center">
                      <h3 className="font-heading font-bold text-xl text-neutral-800">
                        {maireNom || settings?.mayor_name || "Pape Songde Diop"}
                      </h3>
                      <p className="text-neutral-600">Maire de Gandiaye</p>
                    </div>
                  </div>
                </div>
                
                <div className="lg:col-span-2">
                  <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6">
                    Bienvenue à Gandiaye !
                  </h2>
                  <div className="prose prose-lg max-w-none text-neutral-600">
                    {mayorMessage.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                  <div className="mt-8 p-6 bg-emerald-50 rounded-xl border-l-4 border-emerald-500">
                    <p className="text-emerald-800 font-medium italic">
                      "Gandiaye change, Gandiaye avance. C'est unis, dans la solidarité et le travail, que nous ferons rayonner notre belle commune."
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Conseil Municipal */}
            <TabsContent value="conseil">
              <div>
                <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-8">
                  Le Conseil Municipal
                </h2>
                
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loader"></div>
                  </div>
                ) : council.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {council.map((member) => (
                      <Card key={member.id} className="card-teranga">
                        <CardContent className="p-6">
                          {member.photo_url && (
                            <img 
                              src={member.photo_url.startsWith('/api/') ? `${BASE_URL}${member.photo_url}` : member.photo_url}
                              alt={`${member.full_name}, ${member.position}`}
                              loading="lazy"
                              className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
                            />
                          )}
                          <div className="text-center">
                            <h3 className="font-heading font-semibold text-lg">{member.full_name}</h3>
                            <p className="text-emerald-600 font-medium">{member.position}</p>
                            {member.commission && (
                              <p className="text-neutral-500 text-sm mt-1">{member.commission}</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-neutral-50 rounded-xl">
                    <Users className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">Les membres du conseil seront bientôt affichés.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Services */}
            <TabsContent value="services">
              <div>
                <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-8">
                  Services Municipaux
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {services.map((service, index) => {
                    const IconComp = ICON_MAP[service.icon] || FileText;
                    return (
                      <Card key={index} className="card-teranga">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="service-icon flex-shrink-0">
                              <IconComp size={28} />
                            </div>
                            <div>
                              <h3 className="font-heading font-semibold text-lg mb-2">{service.name}</h3>
                              <p className="text-neutral-600 text-sm mb-3">{service.description}</p>
                              <div className="flex items-center gap-2 text-emerald-600 text-sm">
                                <Clock size={14} />
                                <span>{service.hours}</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                <div className="mt-8 p-6 bg-gold-400/10 rounded-xl">
                  <h3 className="font-heading font-semibold text-lg mb-2">Coordonnées</h3>
                  <div className="flex items-center gap-3">
                    <Phone className="text-emerald-600" size={20} />
                    <span className="text-neutral-700">{coordonnees}</span>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Démarches */}
            <TabsContent value="demarches">
              <div>
                <div className="mb-8">
                  <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-2">
                    Vos Démarches Administratives
                  </h2>
                  <p className="text-neutral-600">
                    Simplifiez vos procédures en consultant les pièces à fournir et les délais.
                  </p>
                </div>

                <div className="space-y-8">
                  {procedures.map((category, catIndex) => (
                    <div key={catIndex}>
                      <h3 className="text-xl font-heading font-semibold text-emerald-700 mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-sm">
                          {catIndex + 1}
                        </span>
                        {category.category}
                      </h3>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        {category.items.map((item, itemIndex) => (
                          <Card key={itemIndex} className="bg-white border border-neutral-200 hover:border-emerald-300 transition-colors">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base font-semibold">{item.title}</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-sm text-neutral-500 mb-3">
                                <span className="font-medium text-neutral-700">Délai:</span> {item.delay}
                              </div>
                              <div>
                                <span className="text-sm font-medium text-neutral-700">Pièces à fournir:</span>
                                <ul className="mt-2 space-y-1">
                                  {item.documents.map((doc, docIndex) => (
                                    <li key={docIndex} className="flex items-start gap-2 text-sm text-neutral-600">
                                      <ChevronRight size={14} className="mt-1 text-emerald-500 flex-shrink-0" />
                                      {doc}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-12 p-8 bg-emerald-50 rounded-xl">
                  <h3 className="font-heading font-semibold text-xl mb-4">Guichet Diaspora</h3>
                  <p className="text-neutral-600 mb-4">
                    {guichetDiaspora}
                  </p>
                  <Link to="/diaspora">
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      Accéder à l'Espace Diaspora
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
};

export default MairiePage;
